import { BarChart3, CalendarDays, Clock, LogOut, Menu, Moon, Sun, Users, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '../auth';

const LIGACOES = [
  { para: '/', rotulo: 'Dashboard', icone: BarChart3, exato: true },
  { para: '/funcionarios', rotulo: 'Funcionários', icone: Users },
  { para: '/registo-horas', rotulo: 'Registo de Horas', icone: Clock },
  { para: '/folha-mensal', rotulo: 'Folha Mensal', icone: CalendarDays },
];

function useTema() {
  const [tema, setTema] = useState<'light' | 'dark'>(
    () => (localStorage.getItem('sala.tema') as 'light' | 'dark') ?? 'light',
  );
  useEffect(() => {
    document.documentElement.dataset.theme = tema;
    localStorage.setItem('sala.tema', tema);
  }, [tema]);
  return [tema, () => setTema((t) => (t === 'dark' ? 'light' : 'dark'))] as const;
}

export default function Layout() {
  const { utilizador, sair } = useAuth();
  const [tema, alternarTema] = useTema();
  const [menuAberto, setMenuAberto] = useState(false);

  return (
    <div className="min-h-screen">
      <header className="sem-impressao bg-tinta-900 text-papel-100 shadow-sm">
        <div className="mx-auto flex max-w-[1600px] items-center gap-4 px-4 py-3">
          <div className="flex items-center gap-2.5">
            <Users className="h-6 w-6 shrink-0" aria-hidden />
            <span className="text-sm font-semibold leading-tight">
              Gestão de<br className="hidden sm:block" /> Assistentes de Sala
            </span>
          </div>

          <nav className="ml-4 hidden flex-1 items-center gap-1 lg:flex" aria-label="Principal">
            {LIGACOES.map(({ para, rotulo, icone: Icone, exato }) => (
              <NavLink
                key={para}
                to={para}
                end={exato}
                className={({ isActive }) =>
                  `flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    isActive ? 'bg-marca-500 text-white' : 'text-papel-300 hover:bg-white/10 hover:text-papel-50'
                  }`
                }
              >
                <Icone className="h-4 w-4" aria-hidden />
                {rotulo}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <span className="hidden text-sm text-papel-400 xl:inline">{utilizador?.email}</span>
            <button
              type="button"
              onClick={alternarTema}
              className="rounded-lg p-2 text-papel-300 hover:bg-white/10 hover:text-papel-50"
              aria-label={tema === 'dark' ? 'Mudar para tema claro' : 'Mudar para tema escuro'}
            >
              {tema === 'dark' ? <Sun className="h-4 w-4" aria-hidden /> : <Moon className="h-4 w-4" aria-hidden />}
            </button>
            <button
              type="button"
              onClick={sair}
              className="flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-sm font-medium
                         text-white hover:bg-white/20"
            >
              <LogOut className="h-4 w-4" aria-hidden />
              <span className="hidden sm:inline">Sair</span>
            </button>
            <button
              type="button"
              onClick={() => setMenuAberto((v) => !v)}
              className="rounded-lg p-2 text-papel-300 hover:bg-white/10 lg:hidden"
              aria-label="Menu"
              aria-expanded={menuAberto}
            >
              {menuAberto ? <X className="h-5 w-5" aria-hidden /> : <Menu className="h-5 w-5" aria-hidden />}
            </button>
          </div>
        </div>

        {menuAberto && (
          <nav className="border-t border-white/10 px-4 pb-3 lg:hidden" aria-label="Principal (móvel)">
            {LIGACOES.map(({ para, rotulo, icone: Icone, exato }) => (
              <NavLink
                key={para}
                to={para}
                end={exato}
                onClick={() => setMenuAberto(false)}
                className={({ isActive }) =>
                  `mt-1 flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium ${
                    isActive ? 'bg-marca-500 text-white' : 'text-papel-300 hover:bg-white/10'
                  }`
                }
              >
                <Icone className="h-4 w-4" aria-hidden />
                {rotulo}
              </NavLink>
            ))}
          </nav>
        )}
      </header>

      <main className="mx-auto max-w-[1600px] px-4 py-6">
        <Outlet />
      </main>

      <footer className="sem-impressao mx-auto max-w-[1600px] px-4 pb-6 text-xs text-tinta-400">
        <NavLink to="/privacidade" className="hover:underline">Privacidade</NavLink>
      </footer>
    </div>
  );
}
